import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { DotPulse } from "simplydo/react/components/DotPulse";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H3, H5, Subheader } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Message } from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { updateCurrentUser, useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

const VerifyEmail = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tryAgain, setTryAgain] = useState(10);
  const [editingEmail, setEditingEmail] = useState<string>("");
  const [editingEmailOpen, setEditingEmailOpen] = useState<boolean>(false);
  const hasAutoResentVerification = useRef(false);
  const user = useCurrentUser();

  const { t } = useTranslation();

  useEffect(() => {
    const tryAgainTimer = setInterval(
      () => setTryAgain((prev) => Math.max(prev - 1, 0)),
      1000,
    );
    return () => {
      clearInterval(tryAgainTimer);
    };
  }, []);

  // Ping at regular intervals to user endpoint to see if they have verified their email
  useEffect(() => {
    const userMeInterval = setInterval(() => {
      if (document.visibilityState === "visible") {
        api.users.me(
          "",
          (userResponse: OpenAPI.GET<"/users/me">["response"]) => {
            const me = userResponse as UserMe;
            if (util.hasVerifiedEmail(me, me.ownerOrganisation)) {
              clearInterval(userMeInterval);
              updateCurrentUser(me);
            }
          },
          () => {},
        );
      }
    }, 2000);
    return () => {
      clearInterval(userMeInterval);
    };
  }, []);

  const resendVerificationEmail = useCallback(() => {
    setLoading(true);
    const loadingId = toast.loading(
      t("entry.verifyEmail.resend.statusMessageLoading"),
    );
    api.auth.requestEmailVerification(
      "",
      () => {
        setLoading(false);
        toast.success(t("entry.verifyEmail.resend.statusMessageSuccess"), {
          id: loadingId,
        });
      },
      (err) => {
        setLoading(false);
        toast.error(err.message, { id: loadingId });
      },
    );
  }, [t]);

  useEffect(() => {
    if (!user || user?.emails?.[0]?.verified) {
      return;
    }
    if (hasAutoResentVerification.current) {
      return;
    }
    const now = Date.now();
    // If the email was sent more than 2 days ago, we can resend it
    const verifyEmailSentAtMs = user.verifyEmailSentAt
      ? new Date(user.verifyEmailSentAt).getTime()
      : 0;
    const serverResendDue =
      !verifyEmailSentAtMs || now - verifyEmailSentAtMs > 172800000; // 2 days

    if (!serverResendDue) {
      return;
    }

    resendVerificationEmail();
    hasAutoResentVerification.current = true;
    setTryAgain(30);
  }, [resendVerificationEmail, user]);

  const updateEmailAddress = useCallback(() => {
    api.auth.updateEmailAddress(
      { email: editingEmail },
      () => {
        setLoading(false);
        setTryAgain(30);
        setEditingEmail("");
        setEditingEmailOpen(false);
        api.invalidateQueries(api.users.me);
        resendVerificationEmail();
      },
      (err) => {
        setLoading(false);
        toast.error(err.message);
      },
    );
  }, [editingEmail, resendVerificationEmail]);

  return (
    <>
      <H3>
        {t("entry.verifyEmail.title")}
        <Subheader>{t("entry.verifyEmail.info")}</Subheader>
      </H3>
      <div style={{ display: "flex", gap: 8 }}>
        <Input
          fluid
          size="small"
          label={<Label>{t("entry.verifyEmail.yourEmailLabel")}</Label>}
          readOnly
          value={user?.emails[0]?.address}
        />
        <Popup
          on="click"
          wide
          trigger={
            <Button size="small" loading={loading} variant="outline">
              {t("entry.verifyEmail.changeEmailButton")}
            </Button>
          }
        >
          {
            <div style={{ display: "flex", flexDirection: "column" }}>
              <b>{t("entry.verifyEmail.updateEmail.title")}</b>
              <p>{t("entry.verifyEmail.updateEmail.info")}</p>
              <Input
                value={editingEmail}
                onChange={(_e, { value }) => {
                  setEditingEmail(value);
                }}
                placeholder={t("entry.verifyEmail.yourEmailLabel")}
                action={
                  <Button
                    loading={loading}
                    disabled={
                      editingEmail === user?.emails[0]?.address || !editingEmail
                    }
                    onClick={() => {
                      if (editingEmail === user?.emails[0]?.address) {
                        setEditingEmailOpen(false);
                        return;
                      }
                      updateEmailAddress();
                    }}
                  >
                    {t("generic.save")}
                  </Button>
                }
              />
            </div>
          }
        </Popup>
      </div>
      <Divider section hidden />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
        }}
      >
        <p>
          <strong>{t("entry.verifyEmail.waitingText")}</strong>
        </p>
        <DotPulse size={15} />
      </div>
      <Divider section hidden />

      <div>
        <Divider />
        <H5>
          {t("entry.verifyEmail.resend.title")}

          <Subheader>{t("entry.verifyEmail.resend.info")}</Subheader>
        </H5>
        <Message size="small">
          {t("entry.verifyEmail.resend.spamReminder")}
        </Message>
        <div style={{ display: "flex" }}>
          <Button
            size="small"
            onClick={() => {
              resendVerificationEmail();
              setTryAgain(30);
            }}
            loading={loading}
            disabled={tryAgain > 0}
          >{`${t("entry.verifyEmail.resend.action")}${tryAgain > 0 ? ` (${tryAgain})` : ""}`}</Button>
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
